// payload-dialog.component.ts
// FIXED VERSION - Centered content with working scroll

import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'libs/material.module';

@Component({
  standalone: true,
    selector: 'app-payload-dialog',
    imports: [CommonModule, MaterialModule],
    template: `
    <div class="payload-dialog">
      <!-- Dialog Header -->
      <div class="dialog-header">
        <h2 mat-dialog-title>{{ data.title }}</h2>
        <button mat-icon-button mat-dialog-close class="close-button">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Dialog Content -->
      <mat-dialog-content class="dialog-content">
        
        <!-- Error Message (if applicable) -->
        <div *ngIf="hasError()" class="error-section">
          <mat-card class="error-card">
            <mat-card-header>
              <mat-icon mat-card-avatar color="warn">error</mat-icon>
              <mat-card-title>Error Details</mat-card-title>
            </mat-card-header>
            <mat-card-content>
              <p>{{ getErrorMessage() }}</p>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- JSON Payload Display - Centered with Scrolling -->
        <div class="json-section">
          <mat-card class="json-card">
            <mat-card-header class="json-header">
              <mat-card-title class="json-title">
                <mat-icon>code</mat-icon>
                Payload Data
              </mat-card-title>
              <div class="spacer"></div>
              <button mat-button (click)="copyToClipboard()" [disabled]="copying" class="copy-button">
                <mat-icon>content_copy</mat-icon>
                {{ copying ? 'Copied!' : 'Copy' }}
              </button>
            </mat-card-header>
            <mat-card-content class="json-card-content">
              <div class="json-scroll-container">
                <pre class="json-content">{{ formattedPayload }}</pre>
              </div>
            </mat-card-content>
          </mat-card>
        </div>
      </mat-dialog-content>

      <!-- Dialog Actions -->
      <mat-dialog-actions align="end" class="dialog-actions">
        <button mat-button mat-dialog-close>Close</button>
        <button mat-raised-button color="primary" (click)="downloadPayload()">
          <mat-icon>download</mat-icon>
          Download JSON
        </button>
      </mat-dialog-actions>
    </div>
  `,
    styles: [`
    .payload-dialog {
      min-width: 700px;
      max-width: 1200px;
      width: 85vw;
      height: 80vh;
      display: flex;
      flex-direction: column;
      max-height: 90vh;
    }

    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px 16px 24px;
      border-bottom: 1px solid #e0e0e0;
      flex-shrink: 0;
    }

    .dialog-header h2 {
      margin: 0;
      flex: 1;
      font-size: 20px;
      font-weight: 500;
    }

    .close-button {
      margin-left: 16px;
    }

    .dialog-content {
      flex: 1;
      padding: 24px;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 0; 
    }

    .error-section {
      margin-bottom: 20px;
      flex-shrink: 0;
    }

    .error-card {
      border-left: 4px solid #f44336;
    }

    .json-section {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: stretch;
      min-height: 0; 
    }

    .json-card {
      width: 100%;
      max-width: 900px;
      display: flex;
      flex-direction: column;
      margin: 0 auto;
      min-height: 0; 
    }

    .json-header {
      padding: 16px 20px;
      border-bottom: 1px solid #e0e0e0;
      flex-shrink: 0;
    }

    .json-title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0;
      font-size: 16px;
      font-weight: 500;
    }

    .json-card-content {
      flex: 1;
      padding: 0;
      overflow: hidden;
      min-height: 0; 
    }

    .json-scroll-container {
      height: 100%;
      overflow-y: auto;
      overflow-x: auto;
    }

    .json-content {
      background-color: #f8f9fa;
      border: none;
      border-radius: 0;
      padding: 20px;
      font-family: 'Courier New', monospace;
      font-size: 13px;
      line-height: 1.5;
      white-space: pre-wrap;
      word-break: break-word;
      margin: 0;
      min-height: 100%;
      box-sizing: border-box;
    }

    .spacer {
      flex: 1;
    }

    .copy-button {
      margin-left: 16px;
    }

    .dialog-actions {
      padding: 16px 24px 20px 24px;
      border-top: 1px solid #e0e0e0;
      flex-shrink: 0;
      gap: 12px;
    }

    /* Scrollbar styling */
    .json-scroll-container::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    .json-scroll-container::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    .json-scroll-container::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;
    }

    .json-scroll-container::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }

    @media (max-width: 768px) {
      .payload-dialog {
        min-width: 95vw;
        width: 95vw;
        height: 90vh;
      }
      
      .dialog-content {
        padding: 16px;
      }
      
      .json-content {
        font-size: 12px;
        padding: 16px;
      }
    }

    @media (max-width: 480px) {
      .json-title {
        font-size: 14px;
      }
      
      .json-content {
        font-size: 11px;
        padding: 12px;
      }
    }
  `]
})
export class PayloadDialogComponent {
  formattedPayload: string = '';
  copying: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<PayloadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.formattedPayload = this.formatJson(this.data.payload);
  }

  private formatJson(obj: any): string {
    try {
      return JSON.stringify(obj, null, 2);
    } catch (error) {
      return 'Invalid JSON data';
    }
  }

  hasError(): boolean {
    return this.data.payload?.error || 
           this.data.payload?.status === 'error' ||
           this.data.payload?.issues?.length > 0;
  }

  getErrorMessage(): string {
    if (this.data.payload?.error) {
      return this.data.payload.error;
    }
    if (this.data.payload?.issues?.length > 0) {
      return this.data.payload.issues.join(', ');
    }
    return 'The terms collection is missing the pricing for VOD format and is also missing the SD format.';
  }

  async copyToClipboard(): Promise<void> {
    try {
      this.copying = true;
      await navigator.clipboard.writeText(this.formattedPayload);
      setTimeout(() => {
        this.copying = false;
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      this.copying = false;
    }
  }

  downloadPayload(): void {
    const blob = new Blob([this.formattedPayload], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payload-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}